import * as React from 'react';
import { 
  Box,
  DialogContent,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  MenuItem
} from '@mui/material';

import { useSelector, useDispatch } from 'react-redux'

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField, CSelect } from '../components/common/Modal';
import { schedulePresentation } from '../slices/presentation';
import { scheduleVisualization, updateScheduleVisualization, deleteSchedule } from '../slices/visualization';
import { loadSchedule } from '../slices/visualization';
import { generateTimeIntervals } from '../utils';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ScheduleModal(props) {
  const {open, id, type, handleClose} = props;

  const [mode, setMode] = React.useState('read');
  const [title, setTitle] = React.useState('');
  const [destination, setDestination] = React.useState('');
  const [recurrence, setRecurrence] = React.useState('');
  const [day, setDay] = React.useState('');
  const [time, setTime] = React.useState('');

  const [currentId, setCurrentId] = React.useState(0);

  const dispatch = useDispatch();
  const timeIntervals = generateTimeIntervals();
  const scheduleData = useSelector(state => state.visualization.schedule)

  const handleOk = () => {
    console.log(mode)
    if(mode === 'create') {
      if(title === '' || destination === '' || recurrence === '') {
        window.alert('Please enter valid input')
        return;
      }
      if(type === 'visualization')
        dispatch(scheduleVisualization({id, title, recurrence, day: String(day), time, destinations: [destination]}))
      else
        dispatch(schedulePresentation({presentation: id, title, recurrence, day: String(day), time, destinations: [destination]}))
      setMode('read')
    }
    else if(mode === 'update') {
      if(title === '' || destination === '' || recurrence === '') {
        window.alert('Please enter valid input')
        return;
      }
      if(type === 'visualization')
        dispatch(updateScheduleVisualization({scheduleID: currentId,id, title, recurrence, day: String(day), time, destinations: [destination]}))
      // else
      //   dispatch(schedulePresentation({presentation: id, title, recurrence, day: String(day), time, destinations: [destination]}))
      setMode('read')
    }
    else if(mode === 'read') {
      setMode('create')
      setTitle('');  
      setRecurrence('');
      setDay('');
      setTime('');
      setDestination('');
    }
  }

  const handleUpdate = (sId) => {
    setMode('update');
    setCurrentId(sId);
    scheduleData.forEach(item => {  
      if (item.schedule_id === sId) {  
        setTitle(item.title);  
        setRecurrence(item.recurrence);
        setDay(item.day);
        setTime(item.time);
        setDestination(item.destinations[0]);
      }  
    });
  }
  
  const handleDelete = (sId) => {
    if(window.confirm('Do you really want to remote it?'))
      dispatch(deleteSchedule({scheduleID: sId}))
  }

  React.useEffect(() => {
    if(id === '' || !open) return
    if(mode === 'read') {
      dispatch(loadSchedule({id, mode:type}))
    }
  }, [dispatch, id, mode, open]);

  const handleCloseSchedule = () => {
    setMode('read');
    setCurrentId(0);
    handleClose();
  }

  return (
    <>
      <CDialog
        open={open}
        onClose={handleCloseSchedule}
        maxWidth={"sm"}
        fullWidth
      >
        <CDialogTitle>Scheduled Reports</CDialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseSchedule}
          sx={{
            position: 'absolute',
            right: 6,
            top: 6,
            color: (theme) => theme.palette.grey[800],
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        <DialogContent>
          {
            mode === 'read' ? (
              <Box>
              {
                scheduleData && scheduleData.length>0 && scheduleData.map(item => (
                  <Box 
                    key={item.schedule_id} 
                    sx={{
                      marginX: 2, 
                      marginY: 1, 
                      padding: 2, 
                      border: '1px solid #F3F3F3',
                      justifyContent: 'space-between'
                    }}
                    className='flex align-center'
                  >
                    <Box className='flex flex-column'>
                      <Box sx={{paddingBottom: 1, fontSize:20}}>{item.title}</Box>
                      <Box sx={{color: '#737373', fontSize:12}}>• {item.recurrence} • {item.time} • {item.destinations.length} Recipients</Box>
                    </Box>
                    <Box>
                      <IconButton 
                        size='small' 
                        aria-label="Edit" 
                        sx={{
                          borderRadius: 2, 
                          color: '#737373'
                        }} 
                        onClick={() => handleUpdate(item.schedule_id)}
                      >
                        <EditOutlinedIcon />
                      </IconButton>
                      <IconButton 
                        size='small' 
                        aria-label="Delete" 
                        sx={{
                          borderRadius: 2, 
                          color: '#737373'
                        }} 
                        onClick={() => handleDelete(item.schedule_id)}
                      >
                        <DeleteOutlineOutlinedIcon/>
                      </IconButton>
                    </Box>
                  </Box>
                ))
              }
              {
                scheduleData && scheduleData.length===0 && (
                  <Box>No Schedule Data</Box>
                )
              }
              </Box>
            ) : (
              <>
                <CTextField
                  margin="dense"
                  id="title"
                  name="title"
                  label="Title"
                  type="text"
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={{mt: 0, mb: 0}}
                />
                <Grid container spacing={1}>
                  <Grid item size={4}>
                    <FormControl sx={{mt: 2}} size='small' fullWidth>
                      <InputLabel id={`recurrence-label`} >Recurrence</InputLabel>
                      <CSelect
                        labelId={`recurrence-label`}
                        id="recurrence-type-select"
                        value={recurrence}
                        label="Recurrence"
                        onChange={(e) => setRecurrence(e.target.value)}
                        fullWidth
                        >
                        <MenuItem value={'daily'}>Daily</MenuItem>
                        <MenuItem value={'weekly'}>Weekly </MenuItem>
                        <MenuItem value={'monthly'}>Monthly</MenuItem>
                      </CSelect>
                    </FormControl>
                  </Grid>
                  <Grid item size={4} sx={{display: recurrence === 'daily' ? 'none':'block'}}>
                    <FormControl sx={{mt: 2}} size='small' fullWidth>
                      <InputLabel id={`day-input-label`} >Day</InputLabel>
                      <CSelect
                        labelId={`day-input-label`}
                        id="day-input-select"
                        value={day}
                        label="Day"
                        onChange={(e) => setDay(e.target.value)}
                        fullWidth
                      >
                        {recurrence==='weekly' && (
                          days.map(item => <MenuItem key={`key--${item}`} value={item} >{item}</MenuItem>)
                        )}
                        {recurrence==='monthly' && (
                          Array.from({ length: 31 }, (_, index) => (  
                            <MenuItem key={`key--${index}`} value={index + 1}>  
                              {index + 1}  
                            </MenuItem>  
                          ))
                        )}
                      </CSelect>
                    </FormControl>            
                  </Grid>
                  <Grid item size={4}>
                    <FormControl sx={{mt: 2}} size='small' fullWidth>
                      <InputLabel id={`time-label`} >Time</InputLabel>
                      <CSelect
                        labelId={`time-label`}
                        id="time--select"
                        value={time}
                        label="Time"
                        onChange={(e) => setTime(e.target.value)}
                        fullWidth
                      >
                      {timeIntervals.map((time, index) => (  
                        <MenuItem key={time} value={time}>  
                          {time}  
                        </MenuItem>  
                      ))} 
                      </CSelect>
                    </FormControl>  
                  </Grid>
                </Grid>
                <CTextField
                  margin="dense"
                  id="email"
                  name="email"
                  label="Destination"
                  type="text"
                  size="small"
                  sx={{mt: 2, mb: 0}}
                  fullWidth
                  variant="outlined"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                />    
              </>
            )
          }
        </DialogContent>
        <CDialogActions>
          <CButton 
            onClick={handleClose}
            sx={{textTransform: 'none'}}          
          >
            Cancel
          </CButton>
          <CButton 
            onClick={handleOk}
            sx={{textTransform: 'none'}}
          >
            {mode === 'read' ? 'Create New Schedule': 'OK'}
          </CButton>
        </CDialogActions>
      </CDialog>
    </>
  )
}